/**
 * Web Vitals reporting.
 *
 * Wires Cloudflare Browser Insights (lazy-injected when a token is provided)
 * and reports CLS / INP / LCP / FCP / TTFB to our own telemetry endpoint at
 * /api/v1/telemetry/metric. Production-only — `initWebVitals` is a no-op
 * during dev so we don't pollute the dashboards or noise the API.
 *
 * The token comes from the `VITE_CF_BEACON_TOKEN` env var so deploys can
 * configure it without code changes; if it's unset we still report Web
 * Vitals to our own endpoint and just skip the Cloudflare beacon.
 */
import { type Metric, onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals'

interface VitalReport {
  name: string
  value: number
  id: string
  navigationType?: string
  rating?: string
}

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? 'https://api.ohear.ai'
const VERSION_OVERRIDE = import.meta.env.VITE_CF_WORKER_VERSION_OVERRIDE as string | undefined
const TELEMETRY_PATH = '/api/v1/telemetry/metric'

function sendToTelemetry(metric: Metric): void {
  const report: VitalReport = {
    name: metric.name,
    value: metric.value,
    id: metric.id,
    navigationType: metric.navigationType,
    rating: metric.rating,
  }
  // Use sendBeacon when available — survives page unload, which is when
  // some metrics (CLS / LCP final value) actually fire.
  const body = JSON.stringify({
    name: report.name,
    value: report.value,
    unit: report.name === 'CLS' ? 'gauge' : 'ms',
    tags: {
      id: report.id,
      ...(report.rating ? { rating: report.rating } : {}),
      page: location.pathname,
    },
  })

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      // A Beacon cannot carry the Cloudflare version-override header, so
      // preview builds must use fetch and stay on the 0%-traffic candidate.
      const ok =
        !VERSION_OVERRIDE &&
        navigator.sendBeacon(
          `${API_BASE}${TELEMETRY_PATH}`,
          new Blob([body], { type: 'application/json' }),
        )
      if (ok) return
    }
    void fetch(`${API_BASE}${TELEMETRY_PATH}`, {
      method: 'POST',
      body,
      keepalive: true,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(VERSION_OVERRIDE
          ? { 'Cloudflare-Workers-Version-Overrides': `ohear-api="${VERSION_OVERRIDE}"` }
          : {}),
      },
    }).catch(() => {
      // Telemetry must never break the app.
    })
  } catch {
    // Same — swallow.
  }
}

function injectCloudflareBeacon(token: string): void {
  if (typeof document === 'undefined') return
  if (document.querySelector('script[data-cf-beacon]')) return
  const script = document.createElement('script')
  script.defer = true
  script.src = 'https://static.cloudflareinsights.com/beacon.min.js'
  script.setAttribute('data-cf-beacon', JSON.stringify({ token }))
  document.head.appendChild(script)
}

/**
 * Initialize web vitals + Cloudflare Browser Insights. Production-only;
 * safe to call from dev (no-op).
 */
export function initWebVitals(): void {
  // Vite-style env check — `import.meta.env.PROD` is true in production builds.
  if (!import.meta.env.PROD) return

  const token = (import.meta.env.VITE_CF_BEACON_TOKEN as string | undefined) ?? ''
  if (token && token !== 'YOUR_TOKEN') {
    injectCloudflareBeacon(token)
  }

  onCLS(sendToTelemetry)
  onINP(sendToTelemetry)
  onLCP(sendToTelemetry)
  onFCP(sendToTelemetry)
  onTTFB(sendToTelemetry)
}
